/**
 * AI Service - Tích hợp OpenAI GPT để giải thích nội dung học tập
 * Chỉ trả lời về nội dung cụ thể được gửi, không trả lời các câu hỏi khác
 * Hỗ trợ truy cập database để lấy thông tin đầy đủ về câu hỏi trắc nghiệm
 */

import { BankQuestionService, BankQuestion } from '../bankQuestionService_moi'
import aiUsageService from './AIUsageService'
import { AIUsageFeature } from '../../types/ai'
import { API_CONFIG } from '../../config/api.config'

export interface AIConfig {
  apiKey: string
  model: string
  temperature: number
  maxTokens: number
  maxConcurrentRequests: number
  requestDelay: number
  timeoutSeconds: number
  maxRetries: number
  retryDelay: number
  proxyUrl: string // Proxy URL đã được normalize
}

export interface AIRequest {
  content: string
  contentType: 'theory' | 'quiz' | 'formula' | 'exercise' | 'socratic'
  context?: string
  questionId?: string
  lessonId?: string
  useDatabase?: boolean
  maxTokens?: number // Cho phép override maxTokens cho từng request
  usage?: {
    userId: string
    feature: AIUsageFeature
    metadata?: Record<string, any>
    autoRefundOnError?: boolean
  }
}

export interface AIResponse {
  success: boolean
  explanation?: string
  error?: string
  tokensUsed?: number
  limitReached?: boolean   // server trả 403: đã hết lượt hỏi AI trong ngày
}

class AIService {
  private config: AIConfig
  private requestQueue: Array<() => Promise<void>> = []
  private activeRequests = 0
  private bankService: BankQuestionService

  /**
   * Normalize proxy URL cho dev và production
   * @deprecated - Sử dụng API_CONFIG thay thế
   */
  private normalizeProxyUrl(envProxyUrl: string | undefined): string {
    // Kiểm tra môi trường: dev nếu là DEV mode hoặc hostname là localhost
    const isDev = import.meta.env.DEV ||
      (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))

    // Nếu có env proxy URL, ưu tiên dùng
    if (envProxyUrl && envProxyUrl.trim()) {
      const trimmed = envProxyUrl.trim()

      // Nếu là relative path (bắt đầu bằng /), resolve về current origin
      if (trimmed.startsWith('/')) {
        // Trong production: dùng relative path
        if (!isDev) {
          return trimmed
        }
        // Trong dev: relative path không hoạt động tốt, cần absolute URL
        console.warn('⚠️ Relative proxy URL trong dev mode. Nên dùng: http://localhost:8000/openai-proxy.php')
        return `http://localhost:5173${trimmed}` // Fallback cho dev
      }

      // Nếu đã là absolute URL, dùng luôn
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed
      }

      // Nếu không có protocol, thêm http:// cho dev hoặc https:// cho production
      return isDev ? `http://${trimmed}` : `https://${trimmed}`
    }

    // Fallback: Tự động detect dựa trên môi trường
    if (isDev) {
      // Development: Dùng localhost proxy
      const defaultDevProxy = 'http://localhost:8000/openai-proxy.php'
      console.warn('⚠️ VITE_OPENAI_PROXY_URL chưa được cấu hình!')
      console.warn(`   Tạm thời dùng: ${defaultDevProxy}`)
      console.warn('   Để cấu hình: Set VITE_OPENAI_PROXY_URL=http://localhost:8000/openai-proxy.php trong .env')
      return defaultDevProxy
    } else {
      // Production: Dùng relative path (sẽ resolve về https://ebook.vatli365.vn/openai-proxy.php hoặc Vercel domain)
      const defaultProdProxy = '/openai-proxy.php'
      console.warn('⚠️ VITE_OPENAI_PROXY_URL chưa được cấu hình!')
      console.warn(`   Tạm thời dùng relative path: ${defaultProdProxy}`)
      console.warn('   Để cấu hình: Set VITE_OPENAI_PROXY_URL trong .env.production')
      return defaultProdProxy
    }
  }

  constructor() {
    // ✅ Use centralized API configuration for OpenAI proxy
    const proxyUrl = API_CONFIG.openai.proxyUrl

    if (import.meta.env.DEV) {
      console.log('🔐 AIService Proxy URL:', {
        proxyUrl,
        mode: 'DEVELOPMENT',
        hostname: typeof window !== 'undefined' ? window.location.hostname : 'server-side'
      })
    }

    this.config = {
      apiKey: '', // Không cần - sẽ dùng proxy
      model: 'gpt-4o-mini',
      temperature: 0.1,
      maxTokens: 300,
      maxConcurrentRequests: 2,
      requestDelay: 1.0,
      timeoutSeconds: 30,
      maxRetries: 3,
      retryDelay: 2.0,
      proxyUrl: proxyUrl // Use config proxy URL
    }
    this.bankService = new BankQuestionService()
  }

  /**
   * Lấy thông tin câu hỏi đầy đủ từ database
   */
  private async getQuestionFromDatabase(questionId: string, lessonId: string): Promise<BankQuestion | null> {
    try {
      // Parse lessonId để lấy thông tin lớp, chương, bài
      const lessonInfo = this.parseLessonId(lessonId)
      if (!lessonInfo) {
        console.warn('Cannot parse lessonId:', lessonId)
        return null
      }

      const { lop, chuong, bai } = lessonInfo

      // Lấy tất cả câu hỏi từ database
      const allQuestions = await this.bankService.getAllAvailableQuestions(lop, chuong, bai)

      // Tìm trong tất cả loại câu hỏi
      const allQuestionsList = [
        ...allQuestions.TN,
        ...allQuestions.DS,
        ...allQuestions.TL
      ]

      const question = allQuestionsList.find(q => q.id === questionId)
      return question || null
    } catch (error) {
      console.error('Error getting question from database:', error)
      return null
    }
  }

  /**
   * Parse lessonId để lấy thông tin lớp, chương, bài
   */
  private parseLessonId(lessonId: string): { lop: string, chuong: string, bai: string } | null {
    // Format: lop-chuong-bai (ví dụ: "10-1-1")
    const parts = lessonId.split('-')
    if (parts.length >= 3) {
      return {
        lop: parts[0],
        chuong: parts[1],
        bai: parts[2]
      }
    }
    return null
  }

  /**
   * Format câu hỏi từ database để gửi cho AI
   */
  private formatQuestionForAI(question: BankQuestion): string {
    let formatted = `**Câu hỏi:** ${question.cau_hoi || question.question_lead || ''}\n\n`

    // Thêm các lựa chọn nếu có
    if (question.lua_chon && question.lua_chon.length > 0) {
      formatted += `**Các lựa chọn:**\n`
      question.lua_chon.forEach((option, index) => {
        formatted += `${String.fromCharCode(65 + index)}. ${option}\n`
      })
      formatted += `\n`
    }

    // Thêm đáp án đúng
    if (question.dap_an_dung !== undefined) {
      formatted += `**Đáp án đúng:** ${question.dap_an_dung}\n\n`
    }

    // Thêm lời giải nếu có
    if (question.loi_giai) {
      formatted += `**Lời giải:** ${question.loi_giai}\n\n`
    }

    // Thêm gợi ý nếu có
    if (question.goi_y && question.goi_y.length > 0) {
      formatted += `**Gợi ý:** ${question.goi_y.join(', ')}\n\n`
    }

    // Thêm thông tin hình ảnh nếu có
    if (question.hinh_anh) {
      formatted += `**Hình ảnh câu hỏi:** ${question.hinh_anh}\n`
    }
    if (question.hinh_anh_giai_thich) {
      formatted += `**Hình ảnh giải thích:** ${question.hinh_anh_giai_thich}\n`
    }

    // Thêm thông tin về mức độ và điểm
    formatted += `**Mức độ:** ${question.muc_do || 'Không xác định'}\n`
    formatted += `**Điểm:** ${question.diem || 0}\n`

    return formatted
  }

  /**
   * Tạo prompt phù hợp cho từng loại nội dung
   */
  private normalizeAIResponse(response: string): string {
    if (!response) return response

    // Bước 1: Trim và chuẩn hóa cơ bản
    let normalized = response.trim()

    // Bước 2: Chuẩn hóa các heading (đảm bảo có space sau ###)
    normalized = normalized.replace(/^###\s*/gm, '### ')
    normalized = normalized.replace(/^##\s*/gm, '## ')
    normalized = normalized.replace(/^#\s*/gm, '# ')

    // Bước 3: Xử lý từng dòng để đảm bảo xuống dòng trước heading
    const lines = normalized.split('\n')
    const processedLines: string[] = []

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()

      // Nếu dòng rỗng, skip
      if (!line) {
        // Chỉ thêm dòng rỗng nếu dòng kế là heading
        if (i + 1 < lines.length && lines[i + 1].trim().startsWith('###')) {
          processedLines.push('')
        }
        continue
      }

      // Nếu dòng hiện tại là heading
      if (line.startsWith('### ')) {
        // Đảm bảo có dòng trống phía trước (nếu không phải dòng đầu)
        if (processedLines.length > 0 && processedLines[processedLines.length - 1] !== '') {
          processedLines.push('')  // Thêm dòng trống
        }
        processedLines.push(line)
      } else {
        processedLines.push(line)
      }
    }

    // Bước 4: Join và loại bỏ dòng trống thừa (>2 dòng liên tiếp)
    normalized = processedLines.join('\n')
    normalized = normalized.replace(/\n\n\n+/g, '\n\n')

    return normalized.trim()
  }

  private async createPrompt(request: AIRequest): Promise<string> {
    // Lấy thông tin câu hỏi từ database nếu cần
    let fullQuestionInfo = request.content
    if (request.useDatabase && request.questionId && request.lessonId) {
      const dbQuestion = await this.getQuestionFromDatabase(request.questionId, request.lessonId)
      if (dbQuestion) {
        fullQuestionInfo = this.formatQuestionForAI(dbQuestion)
      }
    }

    // Askbot AI — kiểu Socrates: KHÔNG trả lời thay, chỉ đặt câu hỏi gợi mở để
    // người học tự suy nghĩ và tự đưa ra quyết định.
    if (request.contentType === 'socratic') {
      return `Bạn là Askbot — trợ giảng Vật lí theo phương pháp Socrates.

NGUYÊN TẮC TUYỆT ĐỐI:
- KHÔNG được nêu đáp án, KHÔNG nói phương án nào đúng/sai, KHÔNG giải bài thay.
- Chỉ đặt 2-3 CÂU HỎI NHỎ gợi mở, dẫn dắt người học tự nhớ lại kiến thức và tự quyết định.
- Câu hỏi đi từ bản chất hiện tượng/khái niệm → mối liên hệ với tình huống trong đề.
- Mỗi câu hỏi ngắn (1 dòng), có thể kèm 1 gợi ý nhớ lại kiến thức (không lộ đáp án).
- Tối đa 80 từ. Dùng Markdown: mỗi câu hỏi là 1 dòng bắt đầu bằng "🤔 ". Công thức dùng $...$.
- Kết thúc bằng 1 câu động viên ngắn kiểu "Thử suy nghĩ rồi chọn lại nhé!".

NỘI DUNG CÂU HỎI HỌC SINH ĐANG LÀM:
${fullQuestionInfo}

${request.context ? `BỐI CẢNH: ${request.context}\n` : ''}
Đặt câu hỏi gợi mở (KHÔNG tiết lộ đáp án):`
    }

    const basePrompt = `Bạn là giáo viên Vật lí. Giải thích CỰC KỲ NGẮN GỌN về nội dung được cung cấp.

YÊU CẦU QUAN TRỌNG:
- Tối đa 100 từ
- 3 phần: Lý thuyết, Giải thích, Kết luận
- Dùng Markdown: ### cho tiêu đề, ** cho in đậm
- Công thức dùng $ cho inline: $E = mc^2$
- QUAN TRỌNG: Mỗi ### phải có dòng trống phía trước

FORMAT BẮT BUỘC (ĐÚNG):

### Lý thuyết
Công thức/định luật liên quan

### Giải thích
Phân tích ngắn gọn

### Kết luận
Đáp án/ý chính

NỘI DUNG:
${fullQuestionInfo}

${request.context ? `\nBỐI CẢNH: ${request.context}` : ''}

Trả lời ngắn gọn (<100 từ), NHỚ xuống dòng trước mỗi ###:`

    switch (request.contentType) {
      case 'quiz':
        return basePrompt + '\n\nGiải thích tại sao đáp án đúng/sai.'

      case 'formula':
        return basePrompt + '\n\nGiải thích ý nghĩa công thức.'

      default:
        return basePrompt
    }
  }

  /**
   * Gửi request đến OpenAI API (tự động chọn proxy hoặc direct)
   */
  private async sendRequest(
    prompt: string,
    maxTokensOverride?: number,
    usage?: { userId: string; feature: AIUsageFeature }
  ): Promise<AIResponse> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeoutSeconds * 1000)

    try {
      // ⚠️ Sử dụng proxy URL đã được normalize trong config
      const proxyUrl = this.config.proxyUrl

      if (!proxyUrl) {
        const errorMsg = '❌ Proxy URL chưa được cấu hình!\n\n' +
          'Development: Chạy "php -S localhost:8000" và set VITE_OPENAI_PROXY_URL=http://localhost:8000/openai-proxy.php\n' +
          'Production: Set VITE_OPENAI_PROXY_URL=https://ebook.vatli365.vn/openai-proxy.php hoặc /openai-proxy.php\n\n' +
          'Xem SETUP_LOCAL_DEVELOPMENT.md để biết thêm chi tiết.'
        throw new Error(errorMsg)
      }

      // Chuẩn bị payload cho OpenAI API (kèm userId/feature để SERVER enforce giới hạn AI/ngày)
      const payload = {
        model: this.config.model,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: this.config.temperature,
        max_tokens: maxTokensOverride || this.config.maxTokens,
        ...(usage?.userId ? { userId: usage.userId, feature: usage.feature } : {})
      }

      console.log('🔐 Calling AI via proxy:', proxyUrl)
      console.log('📦 Payload size:', JSON.stringify(payload).length, 'bytes')
      console.log('🎯 Max tokens:', maxTokensOverride || this.config.maxTokens)

      // Kèm JWT (nếu có) để SERVER xác định đúng người dùng khi enforce giới hạn AI/ngày.
      const authToken =
        localStorage.getItem('auth_token') ||
        localStorage.getItem('jwt_token') ||
        localStorage.getItem('token')
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`

      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({} as any))
        const detail = errorData.detail || errorData.error?.message || 'Unknown error'
        // 403 = hết lượt hỏi AI trong ngày → báo rõ, không retry.
        if (response.status === 403) {
          return { success: false, error: detail, limitReached: true }
        }
        throw new Error(`Proxy Error: ${response.status} - ${detail}`)
      }

      const data = await response.json()

      const rawExplanation = data.choices?.[0]?.message?.content || 'Không có phản hồi từ AI'

      // Chuẩn hóa câu trả lời AI
      const explanation = this.normalizeAIResponse(rawExplanation)

      return {
        success: true,
        explanation,
        tokensUsed: data.usage?.total_tokens || 0
      }
    } catch (error) {
      clearTimeout(timeoutId)

      // Xử lý các loại lỗi cụ thể
      if (error instanceof Error) {
        // Lỗi timeout
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: 'Yêu cầu quá thời gian chờ (timeout). Vui lòng thử lại.'
          }
        }

        const msg = error.message;

        // Lỗi 404 - Endpoint not found
        if (msg.includes('404')) {
          return {
            success: false,
            error: `Không tìm thấy AI Service (404). Backend endpoint (${this.config.proxyUrl}) có thể chưa được triển khai hoặc sai cấu hình.`
          }
        }

        // Lỗi 500 - Server Error
        if (msg.includes('500')) {
          return {
            success: false,
            error: `Lỗi Server (500). Có thể do OpenAI API Key chưa được cấu hình hoặc hết hạn mức. Vui lòng kiểm tra log backend.`
          }
        }

        // Lỗi network/fetch
        if (msg.includes('Failed to fetch') ||
          msg.includes('fetch') ||
          msg.includes('NetworkError') ||
          error.name === 'TypeError') {
          const proxyUrl = this.config.proxyUrl
          const isDev = import.meta.env.DEV ||
            (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))

          let errorDetail = 'Không thể kết nối đến AI server.\n\n'

          errorDetail += '**Proxy URL:** ' + proxyUrl + '\n'
          if (isDev) {
            errorDetail += '(Bạn đang ở chế độ Dev, hãy đảm bảo Backend đang chạy ở port 8000)\n'
          }

          return {
            success: false,
            error: errorDetail + `\nChi tiết: ${msg}`
          }
        }

        return {
          success: false,
          error: error.message
        }
      }

      // Lỗi không xác định
      console.error('Unexpected error in sendRequest:', error)
      return {
        success: false,
        error: 'Có lỗi xảy ra khi kết nối với AI. Vui lòng thử lại sau.'
      }
    }
  }

  /**
   * Xử lý queue requests để tránh vượt quá giới hạn
   */
  private async processQueue(): Promise<void> {
    if (this.activeRequests >= this.config.maxConcurrentRequests || this.requestQueue.length === 0) {
      return
    }

    const request = this.requestQueue.shift()
    if (request) {
      this.activeRequests++
      try {
        await request()
      } finally {
        this.activeRequests--
        // Xử lý request tiếp theo sau delay
        setTimeout(() => this.processQueue(), this.config.requestDelay * 1000)
      }
    }
  }

  /**
   * Gửi yêu cầu giải thích nội dung
   */
  async explainContent(request: AIRequest): Promise<AIResponse> {
    return new Promise((resolve) => {
      const executeRequest = async () => {
        let lastError: string | undefined
        const usageContext = request.usage
        // Giới hạn lượt hỏi AI/ngày được CHẶN & GHI NHẬN tại SERVER (/ai/explain) theo userId —
        // không còn consume/revert ở client (tránh đếm trùng & chống lách gọi thẳng API).

        for (let attempt = 1; attempt <= this.config.maxRetries; attempt++) {
          try {
            const prompt = await this.createPrompt(request)
            // Sử dụng maxTokens từ request nếu có, hoặc tăng cho ai_chat feature
            const maxTokens = request.maxTokens ||
              (usageContext?.feature === 'ai_chat' ? 1500 : this.config.maxTokens)
            const response = await this.sendRequest(prompt, maxTokens, usageContext)

            if (response.success) {
              // Server đã ghi nhận lượt này → làm mới số lượt còn lại cho hiển thị.
              if (usageContext?.userId) {
                aiUsageService.getStatus(usageContext.userId, true).catch(() => {})
              }
              resolve(response)
              return
            }

            lastError = response.error
            // Hết lượt (403) → KHÔNG retry, báo ngay.
            if (response.limitReached) {
              resolve(response)
              return
            }
          } catch (error) {
            lastError = error instanceof Error ? error.message : 'Unknown error'
          }

          // Delay trước khi retry
          if (attempt < this.config.maxRetries) {
            await new Promise(resolve => setTimeout(resolve, this.config.retryDelay * 1000))
          }
        }

        resolve({
          success: false,
          error: lastError || 'Đã thử lại nhiều lần nhưng không thành công'
        })
      }

      this.requestQueue.push(executeRequest)
      this.processQueue()
    })
  }

  /**
   * Trích xuất nội dung từ ContentItem để gửi cho AI
   */
  extractContentForAI(item: any, contentType: 'theory' | 'quiz' | 'formula' | 'exercise'): string {
    switch (contentType) {
      case 'theory':
        if (typeof item.content === 'string') {
          return item.content
        } else if (Array.isArray(item.content)) {
          return item.content.map(subItem => {
            if (typeof subItem === 'string') return subItem
            if (subItem.content) return subItem.content
            return JSON.stringify(subItem)
          }).join('\n')
        }
        return JSON.stringify(item)

      case 'quiz':
        const question = item.cau_hoi || item.cauHoi || item.question || item.question_lead || ''
        const options = item.lua_chon || item.luaChon || item.options || []
        const explanation = item.giai_thich || item.giaiThich || item.explanation || ''
        const difficulty = item.do_kho || item.doKho || item.difficulty || ''
        const points = item.diem || item.points || ''

        let quizContent = `Câu hỏi: ${question}`

        if (options && options.length > 0) {
          quizContent += `\n\nCác lựa chọn:\n${options.map((opt: string, idx: number) => `${String.fromCharCode(65 + idx)}. ${opt}`).join('\n')}`
        }

        if (explanation) {
          quizContent += `\n\nGiải thích: ${explanation}`
        }

        if (difficulty) {
          quizContent += `\n\nĐộ khó: ${difficulty}`
        }

        if (points) {
          quizContent += `\n\nĐiểm số: ${points}`
        }

        return quizContent

      case 'formula':
        return `Công thức: ${item.content || item.formula || ''}\n\nGiải thích: ${item.explanation || ''}`

      case 'exercise':
        const problem = item.problem || item.content || ''
        const solution = item.solution || item.answer || ''
        return `Bài tập: ${problem}\n\nLời giải: ${solution}`

      default:
        return JSON.stringify(item)
    }
  }

  /**
   * Kiểm tra kết nối API
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await this.explainContent({
        content: 'Kiểm tra kết nối',
        contentType: 'theory'
      })
      return response.success
    } catch {
      return false
    }
  }
}

// Export singleton instance
export const aiService = new AIService()
export default aiService
